import { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { Clock, Trash2, X } from 'lucide-react';
import { EmptyState } from '@/components/common/EmptyState';
import { useChannelsStore } from '@/store/useChannelsStore';
import { useRecentsStore, formatTimeAgo } from '@/store/useRecentsStore';
import { usePlayerStore } from '@/store/usePlayerStore';
import { useFavoritesStore } from '@/store/useFavoritesStore';

export function RecentPage() {
  const navigate = useNavigate();
  const sportsChannels = useChannelsStore((s) => s.sportsChannels);
  const allChannels = useChannelsStore((s) => s.allChannels);
  const isLoadingInitial = useChannelsStore((s) => s.isLoadingInitial);
  const setCurrentChannel = usePlayerStore((s) => s.setCurrentChannel);
  const currentChannel = usePlayerStore((s) => s.currentChannel);
  const recentEntries = useRecentsStore((s) => s.recentEntries);
  const clearRecents = useRecentsStore((s) => s.clearRecents);
  const removeRecent = useRecentsStore((s) => s.removeRecent);
  const toggleFavorite = useFavoritesStore((s) => s.toggleFavorite);
  const favoriteIds = useFavoritesStore((s) => s.favoriteIds);
  const [confirmingClear, setConfirmingClear] = useState(false);
  // Re-render the relative timestamps every minute so "5m ago" becomes
  // "6m ago" without the user needing to navigate.
  const [, setNow] = useState(Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, []);

  const recentsWithChannels = useMemo(() => {
    if (recentEntries.length === 0) return [];
    const lookup = new Map<string, typeof sportsChannels[number]>();
    for (const c of sportsChannels) lookup.set(c.id, c);
    if (allChannels) for (const c of allChannels) lookup.set(c.id, c);
    // Recents are stored newest-first; we render in that order.
    return recentEntries
      .map((e) => {
        const channel = lookup.get(e.id);
        return channel ? { entry: e, channel } : null;
      })
      .filter((x): x is { entry: typeof recentEntries[number]; channel: typeof sportsChannels[number] } => x !== null);
  }, [recentEntries, sportsChannels, allChannels]);

  const handleSelect = (channel: typeof sportsChannels[number]) => {
    setCurrentChannel(channel);
    navigate('/');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold mb-2">Recently Watched</h1>
          <p className="text-text-muted">
            Your last {recentsWithChannels.length} watched channels. Stored locally on this device.
          </p>
        </div>
        {recentsWithChannels.length > 0 && (
          <div className="flex items-center gap-2">
            {confirmingClear ? (
              <>
                <span className="text-sm text-text-muted">Clear all recents?</span>
                <button
                  onClick={() => {
                    clearRecents();
                    setConfirmingClear(false);
                  }}
                  className="px-3 py-1.5 text-sm bg-error text-white rounded hover:bg-error/90 transition-colors"
                >
                  Yes, clear
                </button>
                <button
                  onClick={() => setConfirmingClear(false)}
                  className="px-3 py-1.5 text-sm bg-bg-elevated border border-border rounded hover:bg-bg transition-colors"
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                onClick={() => setConfirmingClear(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-bg-elevated border border-border rounded hover:bg-bg transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                Clear all
              </button>
            )}
          </div>
        )}
      </div>

      {isLoadingInitial ? (
        <div className="p-8 text-center text-text-muted">Loading channels…</div>
      ) : recentsWithChannels.length === 0 ? (
        <EmptyState
          icon={<Clock className="w-12 h-12" />}
          title="No recents yet"
          description="Channels you watch will appear here. The list is capped at 50 entries."
        />
      ) : (
        <div className="space-y-3">
          {recentsWithChannels.map(({ entry, channel }) => (
            <div
              key={entry.id}
              className="flex items-center gap-3 p-3 bg-bg-elevated border border-border rounded-lg hover:border-accent transition-colors"
            >
              <button
                onClick={() => handleSelect(channel)}
                className="flex items-center gap-3 flex-1 min-w-0 text-left"
              >
                <div className="w-16 h-10 bg-white rounded overflow-hidden flex-shrink-0 flex items-center justify-center">
                  {channel.logo ? (
                    <img
                      src={channel.logo}
                      alt={channel.name}
                      className="w-full h-full object-contain"
                      loading="lazy"
                    />
                  ) : (
                    <span className="text-xs font-semibold text-text-muted">
                      {channel.name.slice(0, 2).toUpperCase()}
                    </span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div
                    className={`font-semibold text-sm truncate ${
                      currentChannel?.id === channel.id ? 'text-accent' : ''
                    }`}
                    title={channel.name}
                  >
                    {channel.name}
                  </div>
                  <div className="text-xs text-text-muted truncate">
                    {channel.country}
                  </div>
                </div>
                <div className="text-xs text-text-muted whitespace-nowrap hidden sm:block">
                  {formatTimeAgo(entry.playedAt)}
                </div>
              </button>
              <button
                type="button"
                onClick={() => toggleFavorite(channel.id)}
                aria-label={
                  favoriteIds.includes(channel.id) ? 'Remove from favorites' : 'Add to favorites'
                }
                className={`p-1.5 rounded transition-colors ${
                  favoriteIds.includes(channel.id)
                    ? 'text-error'
                    : 'text-text-muted hover:text-text'
                }`}
              >
                <svg
                  viewBox="0 0 24 24"
                  className="w-4 h-4"
                  fill={favoriteIds.includes(channel.id) ? 'currentColor' : 'none'}
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                </svg>
              </button>
              <button
                type="button"
                onClick={() => removeRecent(entry.id)}
                aria-label="Remove from recents"
                className="p-1.5 rounded text-text-muted hover:text-text transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
